/**
 * ActionButtons Component
 * Conditional action buttons based on order status
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, XCircle, Package, Calendar, Truck, FileText, Send, CreditCard, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { OrderStatus, PaymentStatus } from '@/types/order';

interface ActionButtonsProps {
  orderStatus: OrderStatus;
  onConfirm: (remarks?: string) => Promise<void>;
  onCancel: (reason: string) => Promise<void>;
  onMarkReady: () => Promise<void>;
  onScheduleDelivery: (deliveryDate: string) => Promise<void>;
  onViewDelivery?: () => void;
  onViewInvoice?: () => void;
  onAssignTransport?: () => void;
  hasTransport?: boolean;
  // Payment props
  paymentStatus?: PaymentStatus;
}

export function ActionButtons({
  orderStatus,
  onConfirm,
  onCancel,
  onMarkReady,
  onScheduleDelivery,
  onViewDelivery,
  onViewInvoice,
  onAssignTransport,
  hasTransport,
  paymentStatus,
}: ActionButtonsProps) {
  // Check if payment is complete
  const isPaymentComplete = paymentStatus === 'PAID' || paymentStatus === 'COMPLETED';
  const isPaymentPending = !paymentStatus || paymentStatus === 'PENDING';
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [confirmRemarks, setConfirmRemarks] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Handle confirm order
   */
  async function handleConfirm() {
    setIsProcessing(true);
    try {
      await onConfirm(confirmRemarks || undefined);
      setShowConfirmDialog(false);
      setConfirmRemarks('');
      toast.success('Order accepted! Payment link sent to buyer.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to accept order');
    } finally {
      setIsProcessing(false);
    }
  }

  /**
   * Handle cancel order
   */
  async function handleCancel() {
    if (!cancelReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }

    setIsProcessing(true);
    try {
      await onCancel(cancelReason);
      setShowCancelDialog(false);
      setCancelReason('');
      toast.success('Order cancelled successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to cancel order');
    } finally {
      setIsProcessing(false);
    }
  }

  /**
   * Handle mark ready
   */
  async function handleMarkReady() {
    setIsProcessing(true);
    try {
      await onMarkReady();
      toast.success('Order marked as ready for dispatch');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to mark order as ready');
    } finally {
      setIsProcessing(false);
    }
  }

  /**
   * Handle schedule delivery
   */
  async function handleScheduleDelivery() {
    if (!deliveryDate) {
      toast.error('Please select a delivery date');
      return;
    }

    setIsProcessing(true);
    try {
      await onScheduleDelivery(deliveryDate);
      setShowScheduleDialog(false);
      setDeliveryDate('');
      toast.success('Delivery scheduled successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to schedule delivery');
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <>
      <div className="flex flex-wrap gap-3">
        {/* ORDER_INITIALIZED/PENDING → Accept to move to CONFIRMED */}
        {(orderStatus === 'PENDING' || orderStatus === 'ORDER_INITIALIZED') && (
          <>
            <Button
              onClick={() => setShowConfirmDialog(true)}
              disabled={isProcessing}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Accept Order
            </Button>
            <Button
              onClick={() => setShowCancelDialog(true)}
              disabled={isProcessing}
              variant="destructive"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Cancel Order
            </Button>
          </>
        )}

        {/* CONFIRMED status - Payment link sent, waiting for payment */}
        {orderStatus === 'CONFIRMED' && (
          <>
            {/* Show payment status indicator */}
            {isPaymentComplete ? (
              <Button
                variant="outline"
                disabled
                className="cursor-not-allowed border-green-200 bg-green-50"
              >
                <CreditCard className="mr-2 h-4 w-4 text-green-600" />
                Payment Complete - Transport Auto-Assigned
              </Button>
            ) : (
              <div className="w-full">
                <Alert className="mb-3 border-blue-200 bg-blue-50">
                  <CreditCard className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 text-sm">
                    Payment link has been sent to buyer. Transport will be automatically assigned after payment is completed.
                  </AlertDescription>
                </Alert>
              </div>
            )}
            {/* Transport Assigned indicator */}
            {hasTransport && (
              <Button
                variant="outline"
                disabled
                className="cursor-not-allowed border-green-200 bg-green-50"
              >
                <Truck className="mr-2 h-4 w-4 text-green-600" />
                Transport Assigned
              </Button>
            )}
            {/* Cancel is still available at CONFIRMED */}
            <Button
              onClick={() => setShowCancelDialog(true)}
              disabled={isProcessing}
              variant="destructive"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Cancel Order
            </Button>
          </>
        )}

        {/* READY → Assign Transport + Schedule Delivery */}
        {orderStatus === 'READY' && (
          <>
            {/* Payment Required Warning - Show when payment is not complete */}
            {!isPaymentComplete && !hasTransport && (
              <div className="w-full">
                <Alert className="mb-3 border-amber-200 bg-amber-50">
                  <CreditCard className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 text-sm">
                    Payment must be completed before assigning transport. Please send the payment link and wait for confirmation.
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* Assign Transport Button - only enabled if payment is complete */}
            {onAssignTransport && !hasTransport && (
              <Button
                onClick={onAssignTransport}
                disabled={isProcessing || !isPaymentComplete}
                className={isPaymentComplete ? "bg-purple-600 hover:bg-purple-700" : "bg-gray-400 cursor-not-allowed"}
                title={!isPaymentComplete ? "Payment must be completed before assigning transport" : "Assign transport for delivery"}
              >
                <Send className="mr-2 h-4 w-4" />
                {isPaymentComplete ? 'Assign Transport' : 'Awaiting Payment'}
              </Button>
            )}
            {hasTransport && (
              <Button
                variant="outline"
                disabled
                className="cursor-not-allowed"
              >
                <Truck className="mr-2 h-4 w-4 text-green-600" />
                Transport Assigned
              </Button>
            )}
            <Button
              onClick={() => setShowScheduleDialog(true)}
              disabled={isProcessing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Calendar className="mr-2 h-4 w-4" />
              Schedule Delivery
            </Button>
          </>
        )}

        {/* TRANSPORT_REQUESTED status - Show transport assigned indicator */}
        {orderStatus === 'TRANSPORT_REQUESTED' && (
          <>
            {hasTransport ? (
              <Button
                variant="outline"
                disabled
                className="cursor-not-allowed border-green-200 bg-green-50"
              >
                <Truck className="mr-2 h-4 w-4 text-green-600" />
                Transport Assigned
              </Button>
            ) : (
              <>
                <div className="w-full">
                  <Alert className="mb-3 border-blue-200 bg-blue-50">
                    <Truck className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800 text-sm">
                      Transport has been requested. Waiting for transport service to process.
                    </AlertDescription>
                  </Alert>
                </div>
                <Button
                  variant="outline"
                  disabled
                  className="cursor-not-allowed border-blue-200 bg-blue-50"
                >
                  <Truck className="mr-2 h-4 w-4 text-blue-600" />
                  Transport Requested
                </Button>
              </>
            )}
          </>
        )}

        {/* IN_TRANSIT → View Delivery */}
        {orderStatus === 'IN_TRANSIT' && onViewDelivery && (
          <Button onClick={onViewDelivery} variant="outline">
            <Truck className="mr-2 h-4 w-4" />
            View Delivery
          </Button>
        )}

        {/* DELIVERED → View Invoice */}
        {orderStatus === 'DELIVERED' && onViewInvoice && (
          <Button onClick={onViewInvoice} variant="outline">
            <FileText className="mr-2 h-4 w-4" />
            View Invoice
          </Button>
        )}
      </div>

      {/* Accept Order Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept Order</DialogTitle>
            <DialogDescription>
              Accepting this order will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Confirm the deal with the customer</li>
                <li>Generate and send a payment link via email</li>
                <li>Generate an invoice</li>
              </ul>
              <p className="mt-2 text-sm text-muted-foreground">
                Transport will be automatically assigned after payment is completed.
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="confirmRemarks">Remarks (Optional)</Label>
              <Textarea
                id="confirmRemarks"
                placeholder="Add any internal remarks..."
                value={confirmRemarks}
                onChange={(e) => setConfirmRemarks(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isProcessing} className="bg-green-600 hover:bg-green-700">
              Accept Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Order</DialogTitle>
            <DialogDescription>
              Please provide a reason for cancelling this order. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cancelReason">Cancellation Reason *</Label>
              <Textarea
                id="cancelReason"
                placeholder="Reason for cancellation..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Go Back
            </Button>
            <Button
              onClick={handleCancel}
              disabled={isProcessing || !cancelReason.trim()}
              variant="destructive"
            >
              Cancel Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Delivery Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Delivery</DialogTitle>
            <DialogDescription>
              Select the delivery date for this order.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="deliveryDate">Delivery Date *</Label>
              <Input
                id="deliveryDate"
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleScheduleDelivery}
              disabled={isProcessing || !deliveryDate}
            >
              Schedule Delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
